import { Button, Popconfirm, Tag } from "antd";
import Table from "components/ResizableTable";
import React, { useMemo } from "react";
import { styled } from "styled-components";
import {
  EditOutlined,
  DeleteOutlined,
  PrinterOutlined,
  ColumnWidthOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import Utils from "utils";
import { deleteNhapXuat } from "store/slices/sale/nhapxuatSlice";
import { deleteSignedDocument } from "store/slices/signatureProcessSlice";
// import { permitKey, PermitValue } from "constants"
import { DOCUMENT_TYPE_ID } from "constants";

export default function TablenhapKhoList({
  setNhapKhoId,
  nhapXuatList,
  setVisibleModal,
  visibleModal,
  reloadData,
  permitList = [],
  setIsPrinter,
  setPrintData,
  setMaKknhap,
  maKknhap,
  setNgaynhap,
  ngaynhap,
  isThue,
  setIsVisibleCopy,
  setRecord,
}) {
  const dispatch = useDispatch();
  // const allowLock = Utils.checkPermitValue(
  //   PermitValue.khoa,
  //   permitList,
  //   permitKey.crm_hospital
  // );
  // const allowEdit = Utils.checkPermitValue(
  //   PermitValue.sua,
  //   permitList,
  //   permitKey.crm_hospital
  // );
  // const allowDelete = Utils.checkPermitValue(
  //   PermitValue.xoa,
  //   permitList,
  //   permitKey.crm_hospital
  // );

  // Các phiếu nhập chung "mahoadon" (>= 2 phiếu) được gom vào 1 dòng cha có thể
  // mở rộng (expand) để xem từng phiếu con, thay vì hiển thị rời rạc như nhau.
  const groupedDataSource = useMemo(() => {
    const list = nhapXuatList || [];
    const countByMaHoaDon = new Map();
    list.forEach((item) => {
      if (item.mahoadon) {
        countByMaHoaDon.set(
          item.mahoadon,
          (countByMaHoaDon.get(item.mahoadon) || 0) + 1
        );
      }
    });

    const result = [];
    const renderedGroups = new Set();
    let stt = 1;

    list.forEach((item) => {
      const isGrouped = item.mahoadon && countByMaHoaDon.get(item.mahoadon) > 1;

      if (!isGrouped) {
        result.push({ ...item, key: item.id ?? item.manx, stt: stt++ });
        return;
      }

      if (renderedGroups.has(item.mahoadon)) return;
      renderedGroups.add(item.mahoadon);

      const groupItems = list.filter((x) => x.mahoadon === item.mahoadon);
      const tongTienNhom = groupItems.reduce(
        (sum, x) => sum + (Number(x.thanhtienxuat) || 0),
        0
      );
      const soDaDuyet = groupItems.filter((x) => x.daduyet).length;

      result.push({
        key: `group-${item.mahoadon}`,
        isGroupHeader: true,
        mahoadon: item.mahoadon,
        sohoadon: item.sohoadon,
        tenkkgiao: item.tenkkgiao,
        tongTienNhom,
        soLuongPhieu: groupItems.length,
        soDaDuyet,
        stt: stt++,
        children: groupItems.map((g) => ({
          ...g,
          key: g.id ?? g.manx,
          isGroupChild: true,
        })),
      });
    });

    return result;
  }, [nhapXuatList]);

  const tableColumns = useMemo(
    () => [
      {
        title: "STT",
        width: 50,
        align: "center",
        render: (_, record) => (record.isGroupChild ? null : record.stt),
      },
      {
        title: "Số phiếu",
        width: 90,
        dataIndex: "sophieu",
        render: (value, record) =>
          record.isGroupHeader ? (
            <span>
              <Tag color="blue">Hóa đơn</Tag> <b>{record.mahoadon}</b>
            </span>
          ) : (
            value
          ),
      },
      {
        title: "Ngày",
        width: 120,
        dataIndex: "ngay",
        render: (value, record) =>
          record.isGroupHeader ? null : Utils.formatDate(value),
      },
      {
        title: "Kho nhập",
        width: 160,
        dataIndex: "tenkk",
        render: (value, record) =>
          record.isGroupHeader
            ? record.sohoadon
              ? `Số HĐ: ${record.sohoadon}`
              : null
            : value,
      },
      {
        title: "Đơn vị giao",
        width: 180,
        dataIndex: "tenkkgiao",
        render: (value, record) =>
          record.isGroupHeader ? <b>{value}</b> : value,
      },
      {
        title: "Đơn vị nhận",
        width: 130,
        dataIndex: "tenkknhan",
        render: (value, record) => (record.isGroupHeader ? null : value),
      },
      {
        title: "Tổng tiền",
        width: 80,
        dataIndex: "thanhtienxuat",
        isMoney: true,
        align: "right",
        render: (value, record) => {
          const amount = record.isGroupHeader ? record.tongTienNhom : value;
          return (
            <span
              style={
                record.isGroupHeader ? { fontWeight: 600, color: "#1677ff" } : undefined
              }
            >
              {amount ? Utils.formatterNumber(amount) : 0}
            </span>
          );
        },
      },
      {
        title: "Người giao",
        width: 80,
        dataIndex: "nguoigiao",
        render: (value, record) => (record.isGroupHeader ? null : value),
      },
      {
        title: "Trạng thái",
        width: 80,
        dataIndex: "daduyet",
        align: "center",
        render: (value, record) => {
          if (record.isGroupHeader) {
            const allApproved = record.soDaDuyet === record.soLuongPhieu;
            const noneApproved = record.soDaDuyet === 0;
            return (
              <Tag color={allApproved ? "green" : noneApproved ? "red" : "orange"}>
                {record.soDaDuyet}/{record.soLuongPhieu} đã duyệt
              </Tag>
            );
          }
          return (
            <Tag color={value ? "green" : "red"}>
              {value ? "Đã duyệt" : "Chưa duyệt"}
            </Tag>
          );
        },
      },
      {
        width: isThue ? 170 : 150,
        fixed: "right",
        align: "center",
        render: (_, record) => {
          if (record.isGroupHeader) return null;
          return (
            <>
              <Button
                title={"Sửa thông tin phiếu"}
                onClick={() => {
                  setVisibleModal(true);
                  setNhapKhoId(record.manx);
                  setMaKknhap(record.makk);
                  setNgaynhap(record.ngay);
                }}
                className="mr-2"
                icon={<EditOutlined />}
                shape="circle"
              />
              <Popconfirm
                title="Bạn muốn xóa phiếu?"
                placement="topLeft"
                onConfirm={() => {
                  dispatch(
                    deleteNhapXuat({
                      id: record.manx,
                      onSuccess: () => {
                        const item = {
                          documentId: record.idphieu,
                          documentTypeId: DOCUMENT_TYPE_ID.NhapHang,
                        };
                        dispatch(deleteSignedDocument(item));
                        reloadData();
                      },
                    })
                  );
                }}
              >
                <Button
                  title={"Xóa phiếu"}
                  className="mr-2"
                  icon={<DeleteOutlined type="primary" />}
                  shape="circle"
                />
              </Popconfirm>
              {/* <Popconfirm
              //disabled={!allowLock}
              title={record.daduyet ? "Hủy duyệt nhập kho?" : "Duyệt nhập kho?"}
              placement="topLeft"
              onConfirm={() => {
                dispatch(
                  changeStateNhapXuat({
                    manx: record.manx,
                    daduyet: record.daduyet,
                    sophieu: record.sophieu,
                    makk: record.makk,
                    makkgiao: record.makkgiao,
                    makknhan: record.makknhan,
                    maloaiphieu: record.maloaiphieu,
                    madoituong: record.madoituong,
                    ngay: record.ngay,
                    nguoigiao: record.nguoigiao,
                    nguoinhan: record.nguoinhan,
                    thue: record.thue,
                    mahoadon: record.mahoadon,
                    sohoadon: record.sohoadon,
                    ngayhoadon: record.ngayhoadon,
                    loai: record.loai,
                    manvduyet: record.manvduyet,
                    manv: record.manv,
                    cachtinhthue: record.cachtinhthue,
                    ghichu: record.ghichu,
                    luanchuyen: false,
                    onSuccess: () => reloadData(),
                  })
                );
              }}
            >
              <Button
                disabled={!allowLock}
                title={
                  allowLock
                    ? record.daduyet
                      ? "Hủy duyệt?"
                      : "Duyệt nhập?"
                    : "Bạn không có quyền thao tác"
                }
                className="mr-2"
                icon={
                  !record.daduyet ? (
                    <CheckCircleOutlined type="primary" />
                  ) : (
                    <StopOutlined type="primary" />
                  )
                }
                shape="circle"
              />
            </Popconfirm> */}
              <Button
                title={"In phiếu"}
                className="mr-2"
                onClick={() => {
                  setIsPrinter(true);
                  setPrintData({
                    idphieu: record.idphieu,
                    manx: record.manx,
                    sophieu: record.sophieu,
                    tenkkgiao: record.makkgiao,
                    tenkknhan: record.tenkknhan,
                  });
                }}
                icon={<PrinterOutlined type="primary" />}
                shape="circle"
              />
              {isThue && (
                <Button
                  title={"Copy sang kho nội bộ"}
                  className="mr-2"
                  onClick={() => {
                    setIsVisibleCopy(true);
                    setRecord(record);
                  }}
                  icon={<ColumnWidthOutlined type="primary" />}
                  shape="circle"
                />
              )}
            </>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nhapXuatList, isThue]
  );

  return (
    <CustomTable
      columns={tableColumns}
      dataSource={groupedDataSource}
      scroll={{ x: "max-content" }}
      rowKey="key"
      expandable={{
        childrenColumnName: "children",
        indentSize: 16,
        rowExpandable: (record) => !!record.isGroupHeader,
      }}
      rowClassName={(record) =>
        record.isGroupHeader ? "nhapkho-group-header-row" : ""
      }
      bordered
    />
  );
}

const CustomTable = styled(Table)`
  .avatar-status:hover {
    text-decoration: underline;
  }
  .nhapkho-group-header-row > td {
    background: #f0f7ff !important;
    font-weight: 500;
  }
`;
